import dayjs from 'dayjs';

import {
  APPOINTMENTS_DIAGNOSIS,
  APPOINTMENTS_SHOW_VISIT_TIME,
  APPOINTMENTS_YEAR,
  EXTENSION_URL,
} from '@constants/extension-codes';
import { APPT_SERVICE_TYPE_MAP } from '@constants/option-types/appointment';

import type { AppointmentValues } from '@models/appointment';
import { AppointmentRecord } from '@models/appointment';

import { isYear } from '@utils/fhir';
import { formatPatientId } from '@utils/format';

import type { AppointmentModelInput, Extension } from 'generated/types';
import {
  ParticipantStatusCode,
  ParticipantTypeCode,
  StatusCode,
} from 'generated/types';

export function getAppointmentTemplate(
  patient_id: string,
  values: AppointmentValues
): AppointmentModelInput {
  const extensionFields: Extension[] = [
    {
      url: `${EXTENSION_URL}${APPOINTMENTS_SHOW_VISIT_TIME}`,
      valueBoolean: values.showTime,
    },
  ];
  if (values.diagnosis) {
    extensionFields.push({
      url: `${EXTENSION_URL}${APPOINTMENTS_DIAGNOSIS}`,
      valueString: values.diagnosis,
    });
  }
  if (values.visitDate && isYear(values.visitDate)) {
    extensionFields.push({
      url: `${EXTENSION_URL}${APPOINTMENTS_YEAR}`,
      valueString: values.visitDate,
    });
  }
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extensionFields.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }

  return {
    resourceType: 'Appointment',
    status: StatusCode.proposed,
    participant: [
      // Patient ID
      {
        actor: { reference: formatPatientId(patient_id) },
        status: ParticipantStatusCode.accepted,
        type: [
          {
            text: 'Patient',
            coding: [
              {
                code: ParticipantTypeCode.PAT,
                system: AppointmentRecord.VITALL_SYSTEM,
              },
            ],
          },
        ],
      },
      // Practitioner
      {
        actor: { display: values.hcpName },
        status: ParticipantStatusCode.accepted,
        type: [
          {
            text: 'Practitioner',
            coding: [
              {
                code: ParticipantTypeCode.PPRF,
                system: AppointmentRecord.VITALL_SYSTEM,
              },
            ],
          },
        ],
      },
    ],
    ...(values.appointmentNotes && {
      comment: values.appointmentNotes,
    }),
    ...(values.followUps && {
      patientInstruction: values.followUps,
    }),
    reasonCode: [
      {
        text: values.title,
      },
    ],
    ...(values.serviceType && {
      serviceType: [
        {
          coding: [
            {
              system: AppointmentRecord.SERVICE_TYPE_SYSTEM,
              code: values.serviceType,
              display: APPT_SERVICE_TYPE_MAP[values.serviceType],
            },
          ],
          text: APPT_SERVICE_TYPE_MAP[values.serviceType],
        },
      ],
    }),
    ...(values.visitDate &&
      !isYear(values.visitDate) && {
        start: AppointmentRecord.getAppointmentStart(values.visitDate),
        end: dayjs(values.visitDate).add(1, 'hour').toISOString(),
      }),
    ...(extensionFields.length && {
      extension: extensionFields,
    }),
  };
}
